<script lang="ts">
/** Локации, где напоминание уже было: живёт до перезагрузки страницы, переходы внутри SPA его не повторяют. */
const shownLocations = new Set<string>();
</script>

<script setup lang="ts">
import { onClickOutside, useEventListener } from '@vueuse/core';
import { onBeforeUnmount, shallowRef, watch } from 'vue';

import { useFloatingPosition } from '@/shared/lib/composables';
import { AppButton, OverlayTeleport } from '@/shared/ui';
import { IconCross } from '@/shared/ui/icons';

/**
 * Напоминание «Смена закрыта» у плашки рейла: раз за загрузку страницы для каждой локации.
 * Гаснет само через 10 секунд или от клика вне неё; наведение и фокус ставят таймер на паузу.
 */
const props = defineProps<{
  /** Плашка, к которой привязана подсказка. */
  anchor: HTMLElement | null;
  /** Локация с закрытой сменой; null — напоминать не о чем. */
  locationId: string | null;
}>();
const emit = defineEmits<{ openShift: [] }>();

const SHOW_DELAY_MS = 1000;
const VISIBLE_MS = 10_000;

const visible = shallowRef(false);
const paused = shallowRef(false);
const { reference, floating, floatingStyles } = useFloatingPosition({
  open: visible,
  placement: 'right-start',
  offset: 12,
  flip: false,
});

watch(
  () => props.anchor,
  (anchor) => {
    reference.value = anchor;
  },
  { immediate: true },
);

let timer: ReturnType<typeof setTimeout> | undefined;
let remaining = VISIBLE_MS;
let startedAt = 0;

function hide(): void {
  clearTimeout(timer);
  visible.value = false;
  paused.value = false;
}

function runCountdown(): void {
  startedAt = Date.now();
  clearTimeout(timer);
  timer = setTimeout(hide, remaining);
}

function pause(): void {
  if (!visible.value || paused.value) return;
  paused.value = true;
  clearTimeout(timer);
  remaining -= Date.now() - startedAt;
}

function resume(): void {
  if (!visible.value || !paused.value) return;
  paused.value = false;
  runCountdown();
}

function openShift(): void {
  hide();
  emit('openShift');
}

watch(
  () => props.locationId,
  (locationId) => {
    hide();
    if (locationId === null || shownLocations.has(locationId)) return;
    timer = setTimeout(() => {
      shownLocations.add(locationId);
      remaining = VISIBLE_MS;
      visible.value = true;
      runCountdown();
    }, SHOW_DELAY_MS);
  },
  { immediate: true },
);

useEventListener(typeof document === 'undefined' ? undefined : document, 'keydown', (event: KeyboardEvent) => {
  if (visible.value && event.key === 'Escape') hide();
});
onClickOutside(floating, hide);
onBeforeUnmount(() => clearTimeout(timer));

defineExpose({ dismiss: hide });
</script>

<template>
  <OverlayTeleport>
    <Transition
      enter-active-class="transition-[opacity,translate] duration-200 ease-out"
      enter-from-class="opacity-0 -translate-x-1.5 motion-reduce:translate-x-0"
      leave-active-class="transition-opacity duration-150 ease-out"
      leave-to-class="opacity-0"
    >
      <div
        v-if="visible"
        ref="floating"
        role="status"
        class="shift-hint z-(--popup-z-index) w-60 rounded-lg border border-(--base-popup-border-color) bg-(--base-popup-bg) p-4 pb-5 text-(--base-popup-text-color) shadow-xl"
        :style="floatingStyles"
        @pointerenter="pause"
        @pointerleave="resume"
        @focusin="pause"
        @focusout="resume"
      >
        <div class="flex items-start gap-2">
          <p class="flex-1 font-semibold">Смена закрыта</p>
          <button
            type="button"
            class="-m-3 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-(--base-popup-secondary-color) hover:text-(--base-popup-text-color)"
            aria-label="Скрыть"
            @click="hide"
          >
            <IconCross
              :width="12"
              :height="12"
            />
          </button>
        </div>
        <p class="mt-1 text-sm text-(--base-popup-secondary-color)">Чтобы принимать оплату, откройте смену.</p>
        <AppButton
          class="mt-3 min-h-11"
          @click="openShift"
        >
          Открыть смену
        </AppButton>
        <span
          class="shift-hint__bar"
          :class="{ 'shift-hint__bar--paused': paused }"
          :style="{ animationDuration: `${VISIBLE_MS}ms` }"
          aria-hidden="true"
        />
      </div>
    </Transition>
  </OverlayTeleport>
</template>

<style scoped>
/* Уголок к плашке: её центр на 22px от верха, подсказка выровнена по её верху. */
.shift-hint::before {
  content: '';
  position: absolute;
  top: 17px;
  left: -6px;
  width: 10px;
  height: 10px;
  background: var(--base-popup-bg);
  border-bottom: 1px solid var(--base-popup-border-color);
  border-left: 1px solid var(--base-popup-border-color);
  transform: rotate(45deg);
}

/* Сколько осталось до исчезновения; пауза синхронна с таймером. */
.shift-hint__bar {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  border-radius: 0 0 0.5rem 0.5rem;
  background: var(--base-accent-color);
  transform-origin: left;
  animation: shift-hint-countdown linear forwards;
}

.shift-hint__bar--paused {
  animation-play-state: paused;
}

@keyframes shift-hint-countdown {
  to {
    transform: scaleX(0);
  }
}
</style>
